import {
  CardBalance,
  CardProduct,
  CardStatuses,
  CardType,
  PaymentCardIdentification,
  CardBalanceProp,
  CardProductAssets,
  CardName,
  PartyIdentification,
} from '../../../../types';
import { formatExpirationDate } from '../../../../contexts/Card';

export class Card {
  cardId: string;
  cardHolderIdentification: PartyIdentification | undefined;
  cardName: CardName;
  description: string;
  cvv: string;
  paymentCardIdentifications: PaymentCardIdentification[];
  partyIdentifications: PartyIdentification[];
  availableBalance: CardBalanceProp;
  pendingAuthorizations: CardBalanceProp;
  cardType: CardType;
  cardNumber: string;
  startDate: string;
  expiryDate: string;
  virtual: boolean;
  balances: CardBalance[];
  cardStatus: CardStatuses;
  cardNetwork: 'VISA' | 'MASTERCARD';
  backgroundFront: string;
  backgroundBack: string;
  backgroundCollapsed: string;
  hasBlackFont: boolean;
  assets: CardProductAssets[];

  constructor(
    cardName: CardName,
    cvv: string,
    description: string,
    paymentCardIdentifications: PaymentCardIdentification[],
    cardType: CardType,
    cardNumber: string,
    startDate: string,
    expiryDate: string,
    virtual: boolean,
    balances: CardBalance[],
    cardStatus: CardStatuses,
    cardProduct: CardProduct,
    cardNetwork: 'VISA' | 'MASTERCARD',
    partyIdentifications: PartyIdentification[],
  ) {
    this.cardId = this.getCardId(paymentCardIdentifications);
    this.cardName = cardName;
    this.description = description;
    this.cvv = cvv;
    this.paymentCardIdentifications = paymentCardIdentifications;
    this.partyIdentifications = partyIdentifications;
    this.cardHolderIdentification = this.getCardHolderIdentification(partyIdentifications);
    this.availableBalance = this.getAvailableBalance(balances);
    this.pendingAuthorizations = this.getPendingAuthorizations(balances);
    this.cardType = cardType;
    this.cardNumber = this.formatMaskedCardNumber(cardNumber);
    this.startDate = startDate;
    this.expiryDate = formatExpirationDate(expiryDate);
    this.virtual = virtual;
    this.balances = balances;
    this.cardStatus = cardStatus;
    this.cardNetwork = cardNetwork;
    this.assets = cardProduct.assets;
    this.backgroundFront = this.getBackground(cardProduct.assets, 'small');
    this.backgroundBack = this.getBackground(cardProduct.assets, 'small'); // TODO add background back
    this.backgroundCollapsed = this.getBackground(cardProduct.assets, 'small-collapsed');
    this.hasBlackFont = this.getHasBlackFont(cardProduct, cardStatus);
  }

  getCardId(paymentCardIdentifications: PaymentCardIdentification[]): string {
    return (
      paymentCardIdentifications.find(
        ({ identifier }) => identifier.issuingAuthority === 'DXB',
      ) as PaymentCardIdentification
    ).identifier.value;
  }

  getCardHolderIdentification(partyIdentifications: PartyIdentification[]): PartyIdentification | undefined {
    return partyIdentifications?.find(({ type }) => type === 'CoreBankingIdentifier');
  }

  getAvailableBalance(balances: CardBalance[]): CardBalanceProp {
    return (balances.find(balance => balance.type === 'AvailableBalance') as CardBalance).balance;
  }

  getPendingAuthorizations(balances: CardBalance[]): CardBalanceProp {
    return (balances.find(balance => balance.type === 'BlockedBalance') as CardBalance).balance;
  }

  formatMaskedCardNumber(value: string): string {
    const parts = [];

    for (let i = 0, len = value.length; i < len; i += 4) {
      parts.push(value.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  }

  getBackground(assets: CardProductAssets[], name: string): string {
    return assets?.find(product => product.name === name)?.path!;
  }

  getHasBlackFont(cardProduct: CardProduct, status: CardStatuses): boolean {
    const value = cardProduct.identifications?.find(identification => identification.type === 'ProductIdentifier')
      ?.identifier?.value;
    return (value === '200001' || value === '200005');
  }
}
